import { createRouter, createWebHistory } from 'vue-router'
import AuthService from '@/services/AuthService'
import LoginPage from '@/views/LoginPage.vue'
import Onboarding from '@/views/Onboarding.vue'
import FormationPage from '@/views/FormationPage.vue'
import Dashboard from '@/views/Dashboard.vue'
import DebugPage from '@/views/DebugPage.vue'
import DinosaurGameDemo from '@/views/DinosaurGameDemo.vue'
import LandingPage from '@/views/LandingPage.vue'
import UserTypeSelection from '@/views/UserTypeSelection.vue'

// Scenarios
import ScenarioPage from '@/views/skillGames/ScenarioGame.vue'
import ResultsPage from '@/views/ResultsPage.vue'

// Hard skills mini games
import GameSpeed from '@/views/skillGames/GameSpeed.vue'
import ShapeSequenceGame from '@/views/skillGames/ShapeSequenceGame.vue'
import ScratchGame from '@/views/skillGames/ScratchGame.vue'
import TinderMetiers from '@/views/skillGames/JobsGame.vue'
import SoudeurCard from '@/components/jobsCards/SoudeurCard.vue'
import JardinerCard from '@/components/jobsCards/JardinerCard.vue'
import CoiffeurCard from '@/components/jobsCards/CoiffeurCard.vue'
import SkillsWheelPage from '@/views/skillGames/SkillsWheelGame.vue'
import Environment from '@/views/skillGames/EnvironmentGame.vue'
import ProfilePage from '@/views/ProfilePage.vue'

// Parcours Accompagnant
import AccompanistDashboard from '@/views/accompaniment/AccompanistDashboard.vue'
import AccompanistProfile from '@/views/accompaniment/AccompanistProfile.vue'
import YoungPersonProfile from '@/views/accompaniment/YoungPersonProfile.vue'
import AccompagnateurOnboarding from '@/views/accompaniment/Onboarding.vue'
import AccompanistLogin from '@/views/accompaniment/AccompanistLogin.vue'
import EmailVerification from '@/views/accompaniment/EmailVerification.vue'
import ForgotPassword from '@/views/accompaniment/ForgotPassword.vue'
import ResetPassword from '@/views/accompaniment/ResetPassword.vue'
import ResourcesPage from '@/views/accompaniment/ResourcesPage.vue'

// Définition des role_id
const ROLES = {
  YOUNG_PERSON: 8,
  ENSEIGNANT: 10,
  PSYCHOLOGUE: 11,
  FAMILLE: 12,
}

// Helper pour créer un tableau de rôles accompagnants
const ACCOMPANIST_ROLES = [ROLES.ENSEIGNANT, ROLES.PSYCHOLOGUE, ROLES.FAMILLE]

const routes = [
  {
    path: '/',
    meta: { requiresAuth: false, public: true },
    name: 'LandingPage',
    component: LandingPage,
  },
  {
    path: '/commencer',
    name: 'UserTypeSelection',
    component: UserTypeSelection,
  },

  // Jeune
  {
    path: '/jeune/onboarding',
    name: 'Onboarding',
    component: Onboarding,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/jeune/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/jeune/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/profile-page',
    name: 'ProfilePage',
    component: ProfilePage,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/game-speed',
    name: 'GameSpeed',
    component: GameSpeed,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/shape-sequence-game',
    name: 'ShapeSequenceGame',
    component: ShapeSequenceGame,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/scratch-coding',
    name: 'ScratchGame',
    component: ScratchGame,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/metiers',
    name: 'TinderMetiers',
    component: TinderMetiers,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/metier/soudeur',
    name: 'SoudeurCard',
    component: SoudeurCard,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/metier/jardinier',
    name: 'JardinerCard',
    component: JardinerCard,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/metier/coiffeur',
    name: 'CoiffeurCard',
    component: CoiffeurCard,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/environment',
    name: 'Environment',
    component: Environment,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/scenarios',
    name: 'ScenarioList',
    redirect: '/jeune/scenarios/inconnu-en-difficulte',
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/scenarios/:urlName',
    name: 'ScenarioPage',
    component: ScenarioPage,
    props: true,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/resultats',
    name: 'ResultsPage',
    component: ResultsPage,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/formation',
    name: 'FormationPage',
    component: FormationPage,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/jeune/roue-des-competences',
    name: 'SkillsWheelPage',
    component: SkillsWheelPage,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/debug',
    name: 'DebugPage',
    component: DebugPage,
    meta: { requiresAuth: true, allowedRoles: [ROLES.YOUNG_PERSON] },
  },
  {
    path: '/dinosaur-demo',
    name: 'DinosaurGameDemo',
    component: DinosaurGameDemo,
    meta: { requiresAuth: false, public: true },
  },
  // Accompagnateur
  {
    path: '/accompagnant/login',
    name: 'AccompanistLogin',
    component: AccompanistLogin,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/accompagnant/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/accompagnant/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/accompagnant/dashboard',
    name: 'AccompanistDashboard',
    component: AccompanistDashboard,
    meta: { requiresAuth: true, allowedRoles: ACCOMPANIST_ROLES },
  },
  {
    path: '/accompagnant/profile',
    name: 'AccompanistProfile',
    component: AccompanistProfile,
    meta: { requiresAuth: true, allowedRoles: ACCOMPANIST_ROLES },
  },
  {
    path: '/accompagnant/young/:id',
    name: 'YoungPersonProfile',
    component: YoungPersonProfile,
    props: true,
    meta: { requiresAuth: true, allowedRoles: ACCOMPANIST_ROLES },
  },
  {
    path: '/accompagnant/resources',
    name: 'ResourcesPage',
    component: ResourcesPage,
    meta: { requiresAuth: true, allowedRoles: ACCOMPANIST_ROLES },
  },
  {
    path: '/accompagnant/onboarding',
    name: 'AccompagnateurOnboarding',
    component: AccompagnateurOnboarding,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: '/auth/verify',
    name: 'EmailVerification',
    component: EmailVerification,
    meta: { requiresAuth: false, public: true },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

// Navigation guard global pour vérifier l'authentification et les rôles
router.beforeEach(async (to, from, next) => {
  // Si la route ne nécessite pas d'authentification, laisser passer
  if (!to.meta.requiresAuth) {
    next()
    return
  }

  try {
    // Vérifier si l'utilisateur est authentifié
    const authStatus = await AuthService.isAuthenticated()

    if (!authStatus.authenticated) {
      // Utilisateur non authentifié, rediriger vers la page de connexion appropriée
      console.log('Accès refusé - Utilisateur non authentifié')

      // Déterminer la page de login appropriée selon la route demandée
      const isAccompanistRoute = to.path.startsWith('/accompagnant')
      const loginRoute = isAccompanistRoute ? 'AccompanistLogin' : 'Login'

      next({
        name: loginRoute,
        query: { redirect: to.fullPath },
      })
      return
    }

    // Utilisateur authentifié, vérifier les rôles
    const userRoleId = parseInt(authStatus.role)
    const allowedRoles = to.meta.allowedRoles

    // Si aucun rôle spécifique n'est requis, laisser passer
    if (!allowedRoles || allowedRoles.length === 0) {
      next()
      return
    }

    // Vérifier si l'utilisateur a un rôle autorisé
    if (allowedRoles.includes(userRoleId)) {
      // Rôle autorisé, laisser passer
      next()
    } else {
      // Rôle non autorisé, rediriger vers le dashboard approprié
      console.log(`Accès refusé - Rôle ${userRoleId} non autorisé pour cette route`)

      // Rediriger vers le dashboard approprié selon le rôle
      if (ACCOMPANIST_ROLES.includes(userRoleId)) {
        // C'est un accompagnant, rediriger vers son dashboard
        next({ name: 'AccompanistDashboard' })
      } else if (userRoleId === ROLES.YOUNG_PERSON) {
        // C'est un jeune, rediriger vers son dashboard
        next({ name: 'Dashboard' })
      } else {
        // Rôle inconnu, déconnecter par sécurité
        console.error('Rôle inconnu:', userRoleId)
        await AuthService.logout()
        next({ name: 'Login' })
      }
    }
  } catch (error) {
    console.error(
      "Erreur lors de la vérification de l'authentification:",
      error,
    )
    // En cas d'erreur, rediriger vers la page de connexion pour sécurité
    const isAccompanistRoute = to.path.startsWith('/accompagnant')
    const loginRoute = isAccompanistRoute ? 'AccompanistLogin' : 'Login'

    next({
      name: loginRoute,
      query: { redirect: to.fullPath },
    })
  }
})

// Exporter les constantes ROLES pour utilisation dans d'autres fichiers
export { ROLES, ACCOMPANIST_ROLES }

export default router
